import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANG } from '@/i18n';
import { pickI18nString } from '@/lib/i18nUtils';

export interface SurveyTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  tool_id: string;
  slug: string;
  is_featured: boolean;
  estimated_duration_minutes: number;
  target_audience: string | null;
  created_at: string;
  updated_at: string;
}

export interface SurveyTemplateWithQuestions extends SurveyTemplate {
  questions: Array<{
    id: string;
    question_text: string;
    is_required: boolean;
    is_reverse_scored: boolean;
    order_index: number;
    options: any;
    question_type: {
      id: string;
      name: string;
      description: string | null;
    };
    focus_areas: Array<{
      id: string;
      name: string;
      description: string | null;
    }>;
  }>;
}

export const useSurveyTemplates = (toolId?: string) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['survey-templates', toolId, lang],
    queryFn: async () => {
      let query = supabase
        .from('survey_templates')
        .select('*')
        .order('is_featured', { ascending: false })
        .order('name');

      if (toolId) {
        query = query.eq('tool_id', toolId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return (
        (data || []).map((t: any) => ({
          ...t,
          name: pickI18nString(t.name_i18n, t.name, lang, DEFAULT_LANG),
          description: pickI18nString(t.description_i18n, t.description, lang, DEFAULT_LANG),
        })) as SurveyTemplate[]
      );
    },
  });
};

export const useSurveyTemplate = (templateId: string) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || DEFAULT_LANG;
  return useQuery({
    queryKey: ['survey-template', templateId, lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('survey_templates')
        .select(`
          *,
          survey_template_questions(
            order_index,
              questions(
                id,
                question_text,
                question_text_i18n,
                is_required,
                is_reverse_scored,
                order_index,
                options,
              question_types!questions_question_type_id_fkey(
                id,
                name,
                name_i18n,
                description,
                description_i18n,
                options,
                options_i18n
              ),
              question_focus_areas(
                focus_areas(
                  id,
                  name,
                  name_i18n,
                  description,
                  description_i18n
                )
              )
            )
          )
        `)
        .eq('id', templateId)
        .single();

      if (error) throw error;

      // Transform the nested data structure with localization
      const template: SurveyTemplateWithQuestions = {
        ...data,
        name: pickI18nString(data.name_i18n, data.name, lang, DEFAULT_LANG),
        description: pickI18nString(data.description_i18n, data.description, lang, DEFAULT_LANG),
        questions: Array.isArray(data.survey_template_questions)
          ? data.survey_template_questions
              .map((tq: any) => ({
                ...tq.questions,
                question_text: pickI18nString(
                  tq.questions.question_text_i18n,
                  tq.questions.question_text,
                  lang,
                  DEFAULT_LANG
                ),
                question_type: {
                  id: tq.questions.question_types.id,
                  name: pickI18nString(
                    tq.questions.question_types.name_i18n,
                    tq.questions.question_types.name,
                    lang,
                    DEFAULT_LANG
                  ),
                  description: pickI18nString(
                    tq.questions.question_types.description_i18n,
                    tq.questions.question_types.description,
                    lang,
                    DEFAULT_LANG
                  ),
                },
                // Resolve options from question_type (i18n first) with fallback to question.options
                options: (() => {
                  const typeOptsI18n = tq.questions.question_types?.options_i18n;
                  const typeOpts = tq.questions.question_types?.options;
                  if (typeOptsI18n && Array.isArray(typeOptsI18n[lang])) return typeOptsI18n[lang];
                  if (Array.isArray(typeOpts)) return typeOpts;
                  return Array.isArray(tq.questions.options) ? tq.questions.options : undefined;
                })(),
                focus_areas:
                  tq.questions.question_focus_areas?.map((qfa: any) => ({
                    ...qfa.focus_areas,
                    name: pickI18nString(
                      qfa.focus_areas.name_i18n,
                      qfa.focus_areas.name,
                      lang,
                      DEFAULT_LANG
                    ),
                    description: pickI18nString(
                      qfa.focus_areas.description_i18n,
                      qfa.focus_areas.description,
                      lang,
                      DEFAULT_LANG
                    ),
                  })) || [],
              }))
              .sort((a: any, b: any) => a.order_index - b.order_index)
          : [],
      } as any;

      return template;
    },
    enabled: !!templateId,
  });
};